import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { DataGrid } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import PageBanner from "../../components/PageBanner";
import {
  editActionRowSx,
  editFormPaperSx,
  editPrimaryColumnSx,
  editSideColumnSx,
  editSplitWrapperSx,
} from "../../components/editPageStyles";
import { COUNTRIES } from "../../constants/countries";
import { FEATURE_FLAGS } from "../../constants/featureFlags";
import { AppConfirmDialog, AppToast } from "../../components/AppFeedback";
import { fetchClientDropdown } from "../../services/clientService";
import { fetchProjectsDropdownByClient } from "../../services/projectService";
import {
  deletePatientDocument,
  fetchPatientDocuments,
  fetchPatientSchedules,
  fetchPatientSubmissions,
  getPatientById,
  getPatientDocumentDownloadUrl,
  getPatientSubmissionAnswersPdfUrl,
  updatePatient,
  updatePatientScheduleDate,
  uploadPatientDocument,
  resyncPatientSchedules,
} from "../../services/patientService";
import { formatDate } from "../../utils/dateFormat";
import PatientSchedulePanel, { ScheduleDateDialog, emptyScheduleDialogState } from "./PatientSchedulePanel";
import PatientQuestionnaireOnBehalfDialog from "./PatientQuestionnaireOnBehalfDialog";
import { buildScheduleDialogState } from "./patientScheduleUtils";
import {
  clearPatientFieldError,
  formatPatientValidationMessage,
  validatePatientForm,
} from "./patientFormUtils";

function toErrorMessage(error, fallback) {
  const raw = error?.message || "";
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed?.detail === "string") return parsed.detail;
  } catch {
    // ignore
  }
  return String(raw || fallback);
}

function formatBytes(bytes) {
  if (!bytes || Number(bytes) <= 0) return "0 B";
  const value = Number(bytes);
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

const doctypeOptions = [
  { value: "clinical", label: "Clinical" },
  { value: "consent", label: "Consent" },
];

const showPatientSubmissions = FEATURE_FLAGS.patientSubmissions.enabled;
const showQuestionnaireOnBehalf = FEATURE_FLAGS.patientQuestionnaireOnBehalf.enabled;

const PatientEdit = () => {
  const navigate = useNavigate();
  const { patientId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedulesLoading, setSchedulesLoading] = useState(false);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  const [clients, setClients] = useState([]);
  const [projectsByClient, setProjectsByClient] = useState([]);
  const [scheduleRows, setScheduleRows] = useState([]);
  const [documentRows, setDocumentRows] = useState([]);
  const [submissionRows, setSubmissionRows] = useState([]);

  const [form, setForm] = useState({
    pid: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    gender: "",
    date_of_birth: "",
    enrollment_date: "",
    email: "",
    phone_local: "",
    address1: "",
    address2: "",
    country: "United States",
    city: "",
    state: "",
    zipcode: "",
    client_id: "",
    project_id: "",
    status: "active",
  });
  const [initialForm, setInitialForm] = useState(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({ doctype: "clinical", document_name: "", file: null });
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const emailDownloadHandledRef = useRef(null);
  const [deleteDocumentId, setDeleteDocumentId] = useState(null);
  const [scheduleDialog, setScheduleDialog] = useState(emptyScheduleDialogState);
  const [scheduling, setScheduling] = useState(false);
  const [onBehalfDialog, setOnBehalfDialog] = useState({
    open: false,
    executionId: null,
    scheduleId: null,
    scheduleLabel: "",
  });

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [resyncing, setResyncing] = useState(false);

  const selectedCountry = useMemo(
    () => COUNTRIES.find((country) => country.name === form.country),
    [form.country]
  );

  const loadProjectsForClient = async (cid) => {
    if (!cid) {
      setProjectsByClient([]);
      return;
    }
    try {
      const items = await fetchProjectsDropdownByClient(cid);
      setProjectsByClient(items || []);
    } catch {
      setProjectsByClient([]);
    }
  };

  const loadSchedulesAndDocuments = async (id) => {
    setSchedulesLoading(true);
    setDocumentsLoading(true);
    if (showPatientSubmissions) {
      setSubmissionsLoading(true);
    }
    try {
      const schedulesPromise = fetchPatientSchedules(id);
      const docsPromise = fetchPatientDocuments(id);
      const submissionsPromise = showPatientSubmissions
        ? fetchPatientSubmissions(id)
        : Promise.resolve([]);

      const [schedules, docs, submissions] = await Promise.all([
        schedulesPromise,
        docsPromise,
        submissionsPromise,
      ]);
      setScheduleRows(
        (schedules || []).map((row) => ({
          id: row.id,
          execution_id: row.execution_id,
          order: row.order,
          schedule_type: row.schedule_type,
          window_open_date: row.window_open_date,
          optimal_date: row.optimal_date,
          window_close_date: row.window_close_date,
          actual_date: row.actual_date,
          status: row.status,
          display_status: row.display_status || "",
          can_schedule: Boolean(row.can_schedule),
        }))
      );
      setDocumentRows((docs || []).map((doc) => ({ ...doc, id: doc.id })));
      if (showPatientSubmissions) {
        setSubmissionRows(
          (submissions || []).map((row) => ({
            ...row,
            id: row.id,
          }))
        );
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to load schedules/documents"),
        severity: "error",
      });
    } finally {
      setSchedulesLoading(false);
      setDocumentsLoading(false);
      if (showPatientSubmissions) {
        setSubmissionsLoading(false);
      }
    }
  };

  const handleResyncSchedules = async () => {
    if (!patientId) return;
    setResyncing(true);
    try {
      await resyncPatientSchedules(patientId);
      setSnackbar({
        open: true,
        message: "Schedules recreated based on current project timeline.",
        severity: "success",
      });
      await loadSchedulesAndDocuments(patientId);
    } catch (error) {
      setSnackbar({
        open: true,
        message: toErrorMessage(error, "Failed to refetch schedules"),
        severity: "error",
      });
    } finally {
      setResyncing(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const loadData = async () => {
      setLoading(true);
      try {
        const [clientItems, patient] = await Promise.all([
          fetchClientDropdown(),
          getPatientById(patientId),
        ]);
        if (cancelled) return;
        setClients(clientItems || []);

        const countryName = patient.country || "United States";
        const countryConfig = COUNTRIES.find((country) => country.name === countryName);
        let phoneLocal = patient.phone || "";
        if (countryConfig?.dialCode && phoneLocal.startsWith(countryConfig.dialCode)) {
          phoneLocal = phoneLocal.slice(countryConfig.dialCode.length);
        }

        const next = {
          pid: patient.pid || "",
          first_name: patient.first_name || "",
          middle_name: patient.middle_name || "",
          last_name: patient.last_name || "",
          gender: patient.gender || "",
          date_of_birth: patient.date_of_birth || "",
          enrollment_date: patient.enrollment_date || "",
          email: patient.email || "",
          phone_local: phoneLocal,
          address1: patient.address1 || "",
          address2: patient.address2 || "",
          country: countryName,
          city: patient.city || "",
          state: patient.state || "",
          zipcode: patient.zipcode || "",
          client_id: patient.client_id || "",
          project_id: patient.project_id || "",
          status: String(patient.status || "").toLowerCase() === "inactive" ? "inactive" : "active",
        };
        setForm(next);
        setInitialForm(next);

        if (next.client_id) {
          await loadProjectsForClient(next.client_id);
        }
        await loadSchedulesAndDocuments(patientId);
      } catch (error) {
        if (!cancelled) {
          setSnackbar({ open: true, message: toErrorMessage(error, "Failed to load subject"), severity: "error" });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    if (patientId) loadData();
    return () => {
      cancelled = true;
    };
  }, [patientId]);

  const handleClientChange = async (cid) => {
    setForm((prev) => ({ ...prev, client_id: cid, project_id: "" }));
    await loadProjectsForClient(cid);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!patientId) return;
    const phoneFull = `${selectedCountry?.dialCode || ""}${(form.phone_local || "").trim()}`;
    const validation = validatePatientForm(form, phoneFull);
    if (!validation.isValid) {
      setFieldErrors(validation.errors);
      setSnackbar({
        open: true,
        message: formatPatientValidationMessage(validation.missingLabels),
        severity: "warning",
      });
      return;
    }
    setFieldErrors({});

    setSaving(true);
    try {
      await updatePatient(patientId, {
        first_name: form.first_name.trim(),
        middle_name: form.middle_name.trim() || null,
        last_name: form.last_name.trim(),
        gender: form.gender || null,
        date_of_birth: form.date_of_birth || null,
        phone: phoneFull || null,
        address1: form.address1.trim() || null,
        address2: form.address2.trim() || null,
        country: form.country || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zipcode: form.zipcode.trim() || null,
        client_id: form.client_id,
        project_id: Number(form.project_id),
        status: form.status || "active",
      });
      setSnackbar({ open: true, message: "Subject updated successfully", severity: "success" });
      setTimeout(() => navigate("/subject-management"), 450);
    } catch (error) {
      setSnackbar({ open: true, message: toErrorMessage(error, "Failed to update subject"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (initialForm) setForm(initialForm);
    setFieldErrors({});
  };

  const handleUploadChange = (event) => {
    const { name, value } = event.target;
    setUploadForm((prev) => ({ ...prev, [name]: value }));
  };

  const setUploadFile = (file) => {
    if (!file) return;
    setUploadForm((prev) => ({
      ...prev,
      file,
      document_name: prev.document_name || file.name,
    }));
  };

  const handleFileInput = (event) => {
    const file = event.target.files?.[0];
    setUploadFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const file = event.dataTransfer?.files?.[0];
    setUploadFile(file);
  };

  const handleUploadDocument = async () => {
    if (!patientId || !uploadForm.file || !uploadForm.document_name.trim()) {
      setSnackbar({ open: true, message: "Document type, name and file are required", severity: "warning" });
      return;
    }
    setUploading(true);
    try {
      await uploadPatientDocument(patientId, {
        doctype: uploadForm.doctype,
        document_name: uploadForm.document_name.trim(),
        file: uploadForm.file,
      });
      setSnackbar({ open: true, message: "Document uploaded", severity: "success" });
      setUploadOpen(false);
      setUploadForm({ doctype: "clinical", document_name: "", file: null });
      await loadSchedulesAndDocuments(patientId);
    } catch (error) {
      setSnackbar({ open: true, message: toErrorMessage(error, "Failed to upload document"), severity: "error" });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async () => {
    if (!patientId || !deleteDocumentId) return;
    try {
      await deletePatientDocument(patientId, deleteDocumentId);
      setDeleteDocumentId(null);
      setSnackbar({ open: true, message: "Document deleted", severity: "success" });
      await loadSchedulesAndDocuments(patientId);
    } catch (error) {
      setDeleteDocumentId(null);
      setSnackbar({ open: true, message: toErrorMessage(error, "Failed to delete document"), severity: "error" });
    }
  };

  const handleDownloadDocument = async (row) => {
    if (!patientId || !row?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(getPatientDocumentDownloadUrl(patientId, row.id), {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error("Failed to download document");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = row.filename || row.document_name || "document";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({ open: true, message: toErrorMessage(error, "Failed to download document"), severity: "error" });
    }
  };

  const downloadSubmissionPdfById = async (submissionId) => {
    if (!patientId || !submissionId) return;
    const token = localStorage.getItem("authToken");
    const response = await fetch(getPatientSubmissionAnswersPdfUrl(patientId, submissionId), {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("Failed to download submission answers PDF");
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `submission-${submissionId}-answers.pdf`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadSubmissionPdf = async (row) => {
    if (!patientId || !row?.id) return;
    try {
      await downloadSubmissionPdfById(row.id);
    } catch (error) {
      setSnackbar({ open: true, message: toErrorMessage(error, "Failed to download submission PDF"), severity: "error" });
    }
  };

  useEffect(() => {
    emailDownloadHandledRef.current = null;
  }, [patientId]);

  useEffect(() => {
    const rawSubmissionId = searchParams.get("downloadSubmission");
    if (!rawSubmissionId || !patientId || loading || submissionsLoading) return;
    if (emailDownloadHandledRef.current === rawSubmissionId) return;

    const submissionId = Number(rawSubmissionId);
    if (!Number.isFinite(submissionId) || submissionId <= 0) return;

    emailDownloadHandledRef.current = rawSubmissionId;

    const clearDownloadParam = () => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("downloadSubmission");
      setSearchParams(nextParams, { replace: true });
    };

    (async () => {
      try {
        await downloadSubmissionPdfById(submissionId);
        setSnackbar({
          open: true,
          message: "Answers PDF downloaded.",
          severity: "success",
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: toErrorMessage(error, "Failed to download submission PDF"),
          severity: "error",
        });
      } finally {
        clearDownloadParam();
      }
    })();
  }, [patientId, loading, submissionsLoading, searchParams, setSearchParams]);

  const formatWindowDate = (row) => {
    if (row.window_open_date && row.window_close_date) {
      return `${formatDate(row.window_open_date)} – ${formatDate(row.window_close_date)}`;
    }
    if (row.window_open_date) return formatDate(row.window_open_date);
    if (row.window_close_date) return formatDate(row.window_close_date);
    return "—";
  };

  const openScheduleDialog = (schedule, sortedSchedules) => {
    setScheduleDialog(buildScheduleDialogState(schedule, sortedSchedules || schedulePreview || []));
  };

  const openOnBehalfDialog = (schedule) => {
    if (!schedule?.execution_id || !schedule?.id) return;
    setOnBehalfDialog({
      open: true,
      executionId: schedule.execution_id,
      scheduleId: schedule.id,
      scheduleLabel: `Schedule ${schedule.schedule} (${String(schedule.type || "").toUpperCase()})`,
    });
  };

  const handleOnBehalfSubmitted = async () => {
    if (!patientId) return;
    await loadSchedulesAndDocuments(patientId);
  };

  const handleScheduleDateSave = async () => {
    if (!patientId || !scheduleDialog.scheduleId || !scheduleDialog.selectedDate) return;
    setScheduling(true);
    try {
      await updatePatientScheduleDate(patientId, scheduleDialog.scheduleId, scheduleDialog.selectedDate);
      setSnackbar({ open: true, message: "Schedule date updated", severity: "success" });
      setScheduleDialog(emptyScheduleDialogState);
      await loadSchedulesAndDocuments(patientId);
    } catch (error) {
      setSnackbar({ open: true, message: toErrorMessage(error, "Failed to update schedule date"), severity: "error" });
    } finally {
      setScheduling(false);
    }
  };

  const schedulePreview = useMemo(
    () =>
      [...(scheduleRows || [])]
        .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
        .map((row) => ({
          id: row.id,
          execution_id: row.execution_id,
          schedule_id: row.id,
          schedule: row.order,
          type: row.schedule_type,
          status: row.status,
          display_status: row.display_status || "pending",
          can_schedule: Boolean(row.can_schedule),
          optimal_date: row.optimal_date,
          window_open: row.window_open_date,
          window_close: row.window_close_date,
          scheduled_date: row.actual_date,
        })),
    [scheduleRows]
  );

  const documentColumns = [
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 130,
      flex: 0.8,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <Tooltip title="Download">
            <IconButton size="small" color="primary" onClick={() => handleDownloadDocument(params.row)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteDocumentId(params.row.id)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    { field: "doctype", headerName: "Type", minWidth: 120, flex: 0.7 },
    { field: "document_name", headerName: "Document Name", minWidth: 180, flex: 1.2 },
    { field: "filename", headerName: "Filename", minWidth: 170, flex: 1.1 },
    { field: "byte_size", headerName: "Size", minWidth: 90, flex: 0.6, renderCell: (params) => formatBytes(params.value) },
  ];

  const submissionColumns = [
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      minWidth: 90,
      flex: 0.5,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Download answers PDF">
          <span>
            <IconButton
              size="small"
              color="primary"
              disabled={!params.row.has_answers_pdf}
              onClick={() => handleDownloadSubmissionPdf(params.row)}
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    { field: "project_name", headerName: "Project Name", minWidth: 160, flex: 1 },
    { field: "questionnaire_name", headerName: "Questionnaire", minWidth: 150, flex: 1 },
    {
      field: "optimal_date",
      headerName: "Optimal Date",
      minWidth: 120,
      flex: 0.8,
      renderCell: (params) => (params.value ? formatDate(params.value) : "—"),
    },
    {
      field: "window_date",
      headerName: "Window Date",
      minWidth: 190,
      flex: 1.1,
      valueGetter: (_, row) => formatWindowDate(row),
    },
    {
      field: "timing_status",
      headerName: "Status",
      minWidth: 120,
      flex: 0.7,
      renderCell: (params) => {
        const isLate = String(params.value || "").toLowerCase() === "late";
        return (
          <Chip
            size="small"
            label={isLate ? "Late submission" : "On time"}
            color={isLate ? "error" : "success"}
            variant="filled"
          />
        );
      },
    },
  ];

  return (
    <Box sx={{ bgcolor: "#f5f5f5", minHeight: "100vh" }}>
      <PageBanner title={`Subject / ${form.pid || patientId || "Details"}`} />

      <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3 }, pb: 4 }}>
        <Box sx={{ mt: 3, mb: 3 }}>
          <Button startIcon={<ArrowBackIcon />} variant="text" onClick={() => navigate("/subject-management")} sx={{ mb: 2 }}>
            Back to Subject Management
          </Button>
          <Typography variant="h5" fontWeight="bold">Edit Subject</Typography>
        </Box>

        {loading ? (
          <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          </Paper>
        ) : (
          <Box sx={editSplitWrapperSx}>
            <Paper elevation={3} sx={editPrimaryColumnSx}>
              <Box component="form" onSubmit={handleSubmit}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Basic Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="PID" value={form.pid} disabled />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      select
                      label="Client"
                      value={form.client_id}
                      error={Boolean(fieldErrors.client_id)}
                      helperText={fieldErrors.client_id}
                      onChange={(e) => {
                        handleClientChange(e.target.value);
                        clearPatientFieldError(setFieldErrors, "client_id");
                      }}
                    >
                      {(clients || []).map((c) => (
                        <MenuItem key={c.cid} value={c.cid}>{c.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      select
                      label="Project"
                      value={form.project_id}
                      error={Boolean(fieldErrors.project_id)}
                      helperText={fieldErrors.project_id}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, project_id: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "project_id");
                      }}
                    >
                      {(projectsByClient || []).map((p) => (
                        <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      label="First Name"
                      value={form.first_name}
                      error={Boolean(fieldErrors.first_name)}
                      helperText={fieldErrors.first_name}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, first_name: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "first_name");
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth label="Middle Name" value={form.middle_name} onChange={(e) => setForm((prev) => ({ ...prev, middle_name: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      label="Last Name"
                      value={form.last_name}
                      error={Boolean(fieldErrors.last_name)}
                      helperText={fieldErrors.last_name}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, last_name: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "last_name");
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      select
                      label="Gender"
                      value={form.gender}
                      error={Boolean(fieldErrors.gender)}
                      helperText={fieldErrors.gender}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, gender: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "gender");
                      }}
                    >
                      <MenuItem value="">Select</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Date of Birth"
                      format="MM/DD/YYYY"
                      value={form.date_of_birth ? dayjs(form.date_of_birth) : null}
                      onChange={(value) => {
                        setForm((prev) => ({
                          ...prev,
                          date_of_birth: value && value.isValid() ? value.format("YYYY-MM-DD") : "",
                        }));
                        clearPatientFieldError(setFieldErrors, "date_of_birth");
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          required: true,
                          error: Boolean(fieldErrors.date_of_birth),
                          helperText: fieldErrors.date_of_birth,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <DatePicker
                      label="Enrollment date"
                      format="MM/DD/YYYY"
                      value={form.enrollment_date ? dayjs(form.enrollment_date) : null}
                      onChange={() => {}}
                      disabled
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: "Cannot be changed after enrollment; first static visit uses this date.",
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField fullWidth select label="Subject Status" value={form.status} onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}>
                      <MenuItem value="active">Active (Auto)</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                    </TextField>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Contact Information</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      type="email"
                      label="Email"
                      value={form.email}
                      disabled
                      error={Boolean(fieldErrors.email)}
                      helperText={fieldErrors.email || "Cannot be edited after the subject is created."}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      select
                      label="Country"
                      value={form.country}
                      error={Boolean(fieldErrors.country)}
                      helperText={fieldErrors.country}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, country: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "country");
                      }}
                    >
                      {COUNTRIES.map((country) => (
                        <MenuItem key={country.code} value={country.name}>{country.name}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth label="Country Code" value={selectedCountry?.dialCode || ""} disabled />
                  </Grid>
                  <Grid item xs={12} md={9}>
                    <TextField
                      required
                      fullWidth
                      label="Mobile Number"
                      value={form.phone_local}
                      error={Boolean(fieldErrors.phone)}
                      helperText={fieldErrors.phone}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, phone_local: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "phone");
                      }}
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>Address</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      required
                      fullWidth
                      label="Address 1"
                      value={form.address1}
                      error={Boolean(fieldErrors.address1)}
                      helperText={fieldErrors.address1}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, address1: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "address1");
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField fullWidth label="Address 2" value={form.address2} onChange={(e) => setForm((prev) => ({ ...prev, address2: e.target.value }))} />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      label="City"
                      value={form.city}
                      error={Boolean(fieldErrors.city)}
                      helperText={fieldErrors.city}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, city: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "city");
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      label="State"
                      value={form.state}
                      error={Boolean(fieldErrors.state)}
                      helperText={fieldErrors.state}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, state: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "state");
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      required
                      fullWidth
                      label="Zipcode"
                      value={form.zipcode}
                      error={Boolean(fieldErrors.zipcode)}
                      helperText={fieldErrors.zipcode}
                      onChange={(e) => {
                        setForm((prev) => ({ ...prev, zipcode: e.target.value }));
                        clearPatientFieldError(setFieldErrors, "zipcode");
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={editActionRowSx}>
                  <Button variant="outlined" onClick={handleReset}>Reset</Button>
                  <Button variant="contained" type="submit" disabled={saving}>{saving ? "Saving..." : "Update Subject"}</Button>
                </Box>
              </Box>
            </Paper>

            <Box sx={editSideColumnSx}>
              <Paper
                elevation={3}
                sx={{ ...editFormPaperSx, minWidth: 0, overflow: "hidden" }}
              >
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Schedules
                  </Typography>
                  {(schedulePreview || []).length === 0 && form.project_id && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleResyncSchedules}
                      disabled={resyncing}
                    >
                      {resyncing ? "Refetching..." : "Refetch Schedules"}
                    </Button>
                  )}
                </Box>
                <PatientSchedulePanel
                  schedules={schedulePreview || []}
                  loading={schedulesLoading}
                  onScheduleClick={(schedule, sortedSchedules) =>
                    openScheduleDialog(schedule, sortedSchedules)
                  }
                  onAnswerOnBehalf={showQuestionnaireOnBehalf ? openOnBehalfDialog : undefined}
                />
              </Paper>

              <Paper elevation={3} sx={editFormPaperSx}>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Subject Documents
                  </Typography>
                  <Button
                    variant="contained"
                    size="small"
                    startIcon={<UploadFileIcon />}
                    onClick={() => setUploadOpen(true)}
                    sx={{ maxWidth: "100%" }}
                  >
                    Upload
                  </Button>
                </Box>
                <DataGrid
                  rows={documentRows}
                  columns={documentColumns}
                  loading={documentsLoading}
                  autoHeight
                  disableRowSelectionOnClick
                  pageSizeOptions={[5, 10]}
                  initialState={{ pagination: { paginationModel: { page: 0, pageSize: 5 } } }}
                  sx={{
                    "& .MuiDataGrid-row": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected": {
                      backgroundColor: "#ffffff",
                    },
                    "& .MuiDataGrid-row.Mui-selected:hover": {
                      backgroundColor: "#ffffff",
                    },
                  }}
                />
              </Paper>

              {showPatientSubmissions ? (
                <Paper elevation={3} sx={{ p: 2.5, borderRadius: 3 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                    Submissions
                  </Typography>
                  <DataGrid
                    rows={submissionRows}
                    columns={submissionColumns}
                    loading={submissionsLoading}
                    autoHeight
                    disableRowSelectionOnClick
                    pageSizeOptions={[5, 10]}
                    initialState={{ pagination: { paginationModel: { page: 0, pageSize: 5 } } }}
                    sx={{
                      "& .MuiDataGrid-row": {
                        backgroundColor: "#ffffff",
                      },
                      "& .MuiDataGrid-row:hover": {
                        backgroundColor: "#ffffff",
                      },
                      "& .MuiDataGrid-row.Mui-selected": {
                        backgroundColor: "#ffffff",
                      },
                      "& .MuiDataGrid-row.Mui-selected:hover": {
                        backgroundColor: "#ffffff",
                      },
                    }}
                  />
                </Paper>
              ) : null}
            </Box>
          </Box>
        )}
      </Container>

      <Dialog
        open={uploadOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          setUploadOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pr: 6 }}>
          Add Subject Document
          <IconButton
            aria-label="close"
            onClick={() => setUploadOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: "grid", gap: 2 }}>
            <TextField
              select
              name="doctype"
              label="Document Type"
              value={uploadForm.doctype}
              onChange={handleUploadChange}
              fullWidth
            >
              {doctypeOptions.map((item) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              name="document_name"
              label="Document Name"
              value={uploadForm.document_name}
              onChange={handleUploadChange}
              fullWidth
            />

            <Box
              onDragEnter={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setDragActive(true);
              }}
              onDragLeave={(event) => {
                event.preventDefault();
                setDragActive(false);
              }}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              sx={{
                border: "1px dashed #9aa4b2",
                borderRadius: 2,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
                bgcolor: dragActive ? "#eef4ff" : "transparent",
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                Drag and drop file here, or click to select
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ wordBreak: "break-all" }}>
                {uploadForm.file ? `Selected: ${uploadForm.file.name}` : "No file selected"}
              </Typography>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: "none" }}
                onChange={handleFileInput}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button onClick={handleUploadDocument} variant="contained" disabled={uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <ScheduleDateDialog
        open={scheduleDialog.open}
        onClose={() => setScheduleDialog(emptyScheduleDialogState)}
        onSelectedDateChange={(selectedDate) =>
          setScheduleDialog((prev) => ({ ...prev, selectedDate }))
        }
        onSave={handleScheduleDateSave}
        scheduling={scheduling}
        scheduleDialog={scheduleDialog}
      />

      <PatientQuestionnaireOnBehalfDialog
        open={showQuestionnaireOnBehalf && onBehalfDialog.open}
        onClose={() =>
          setOnBehalfDialog({
            open: false,
            executionId: null,
            scheduleId: null,
            scheduleLabel: "",
          })
        }
        patientId={patientId}
        executionId={onBehalfDialog.executionId}
        scheduleId={onBehalfDialog.scheduleId}
        scheduleLabel={onBehalfDialog.scheduleLabel}
        onSubmitted={handleOnBehalfSubmitted}
      />

      <AppConfirmDialog
        open={Boolean(deleteDocumentId)}
        title="Delete Document"
        content="Are you sure you want to delete this document?"
        confirmLabel="Delete"
        confirmColor="error"
        onCancel={() => setDeleteDocumentId(null)}
        onConfirm={handleDeleteDocument}
      />

      <AppToast
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      />
    </Box>
  );
};

export default PatientEdit;
